/**
 * Shared Shopify GraphQL helpers for product queries.
 * Used by GraphqlTester and Products quick-view comparison.
 *
 * When Shopify releases a new API version (quarterly), update SHOPIFY_API_VERSION here
 * so the whole app uses it in one place. See: https://shopify.dev/docs/api/usage/versioning
 */

import axios from 'axios'

/** Single source of truth for Shopify Admin API version (e.g. 2026-01). Update when upgrading. */
export const SHOPIFY_API_VERSION = '2025-07'

const DEFAULT_PAGE_SIZES = {
  metafieldsPageSize: 25,
  variantsPageSize: 25,
  mediaPageSize: 25,
}

/**
 * Returns the product GraphQL query with metafields, variants, media, and variant metafields.
 * Compatible with Shopify Admin API (see SHOPIFY_API_VERSION).
 */
export function getProductQuery() {
  const variables = [
    '$id: ID!',
    '$metafieldsPageSize: Int = 25',
    '$variantsPageSize: Int = 25',
    '$mediaPageSize: Int = 25',
  ]
  const queryFields = [
    'id',
    'title',
    'handle',
    'status',
    'createdAt',
    'updatedAt',
    'descriptionHtml',
    'vendor',
    'productType',
    'tags',
    `metafields(first: $metafieldsPageSize) {
      edges {
        node {
          id
          namespace
          key
          type
          value
          createdAt
          updatedAt
        }
      }
    }`,
  ]

  const variantFields = [
    'id',
    'title',
    'sku',
    'barcode',
    'price',
    'compareAtPrice',
    'inventoryQuantity',
    'inventoryPolicy',
    `selectedOptions {
        name
        value
      }`,
    `metafields(first: 10) {
        edges {
          node {
            id
            namespace
            key
            type
            value
          }
        }
      }`,
    `inventoryItem {
        id
        sku
        countryCodeOfOrigin
        tracked
        createdAt
        updatedAt
        measurement {
          weight {
            value
            unit
          }
        }
      }`,
    `media(first: 10) {
        edges {
          node {
            mediaContentType
            alt
            preview {
              image {
                url
              }
            }

            ... on MediaImage {
              id
              image {
                url
              }
            }

            ... on Video {
              id
              duration
              status
              originalSource {
                url
                format
              }
            }

            ... on ExternalVideo {
              id
              originUrl
            }
          }
        }
      }`,
  ]

  queryFields.push(`variants(first: $variantsPageSize) {
      edges {
        node {
          ${variantFields.join('\n          ')}
        }
      }
    }`)

  queryFields.push(`media(first: $mediaPageSize) {
      edges {
        node {
          mediaContentType
          alt
          preview {
            image {
              url
            }
          }

          ... on MediaImage {
            id
            image {
              url
            }
          }

          ... on Video {
            id
            duration
            status
            originalSource {
              url
              format
            }
          }

          ... on ExternalVideo {
            id
            originUrl
          }
        }
      }
    }`)

  return `query ProductWithDetails(
  ${variables.join(',\n  ')}
) {
  product(id: $id) {
    ${queryFields.join('\n    ')}
  }
}`
}

/**
 * Fetches a single product from Shopify via the admin GraphQL execute endpoint.
 * @param {number} shopId - Shop ID (logged-in shop)
 * @param {string|number} productId - Shopify product ID (numeric, e.g. from product.shopify_id)
 * @param {string} [apiVersion] - Shopify API version (defaults to SHOPIFY_API_VERSION)
 * @returns {Promise<{ success: boolean, data?: object, error?: string }>}
 */
export async function fetchShopifyProduct(shopId, productId, apiVersion = SHOPIFY_API_VERSION) {
  const query = getProductQuery()
  const variables = {
    id: `gid://shopify/Product/${productId}`,
    ...DEFAULT_PAGE_SIZES,
  }
  const payload = {
    shop_id: shopId,
    api_version: apiVersion,
    query,
    variables,
    query_type: 'product',
  }
  const { data } = await axios.post('admin/shopify/graphql/execute', payload)
  return data
}
